/* eslint-disable react/prop-types */
import React, { useState } from "react";
import {
  Alert,
  Box,
  Card,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import { days, estimatedCost, idOf, nameOf } from "./smeUtils";
import Typography from "components/ArgonTypography";
import Button from "components/ArgonButton";
import { Choice } from "./SMEControls";

const money = (value) =>
  value == null || value === "" ? "Not available" : `₹${Number(value).toLocaleString("en-IN")}`;
const display = (value) => (value == null ? "Not available" : value);
function Records({ headings, rows }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {headings.map((h) => (
              <TableCell key={h}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length ? (
            rows.map((row, i) => (
              <TableRow key={i}>
                {row.map((cell, j) => (
                  <TableCell key={j}>{cell}</TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={headings.length}>No records available.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
// API adapter boundary: assignments may contain populated tutor/subject/slot objects.
export default function SMEDetails({ data }) {
  const [selected, setSelected] = useState("ALL");
  const assignments = data.tutorAssignments || [];
  const keyOf = (a) => idOf(a._id || a.localId);
  const visible =
    selected === "ALL" ? assignments : assignments.filter((a) => keyOf(a) === selected);
  const attendance = data.attendance || [];
  const history = data.paymentHistory || [];
  const matches = (record) => selected === "ALL" || idOf(record.tutorAssignmentId) === selected;
  const tutorName = (record) =>
    nameOf(
      assignments.find((a) => keyOf(a) === idOf(record.tutorAssignmentId))?.tutorId || record.tutor
    );
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        SME saving coming soon. Attendance and payment
        changes are unavailable.
      </Alert>
      <Typography variant="h6" gutterBottom>
        Assigned tutors ({assignments.length})
      </Typography>
      {!assignments.length && <Typography>No tutors assigned.</Typography>}
      <Grid container spacing={2}>
        {assignments.map((a) => (
          <Grid item xs={12} md={6} key={keyOf(a)}>
            <Card
              sx={{
                p: 3,
                borderRadius: "10px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                border: "1px solid #f5f5f5",
              }}
            >
              <Typography variant="subtitle1">{nameOf(a.tutorId)}</Typography>
              <Typography variant="body2">
                {(a.subjects || []).map(nameOf).join(", ") || "Subjects unavailable"}
              </Typography>
              <Typography variant="body2">
                {(a.classDays || []).map((d) => days[d]).join(", ")} ·{" "}
                {a.slot?.startTime || a.finalisedActiveSlot?.startTime || "Time unavailable"} –{" "}
                {a.slot?.endTime || a.finalisedActiveSlot?.endTime || ""}
              </Typography>
              <Typography variant="body2">
                {a.allocatedClasses} allocated classes · {a.duration} min
              </Typography>
              <Typography variant="body2">
                Per Session Fee (T): {money(a.payRate)}
              </Typography>
              <Typography variant="body2">Estimated cost: {money(estimatedCost(a))}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ my: 3, maxWidth: 440 }}>
        <Choice
          label="Tutor filter"
          value={selected}
          onChange={(value) => setSelected(value || "ALL")}
          options={[
            { value: "ALL", label: "All Tutors" },
            ...assignments.map((a, i) => ({
              value: keyOf(a),
              label: `${nameOf(a.tutorId)} · Assignment ${i + 1}`,
            })),
          ]}
        />
      </Box>
      <Typography variant="h6">Attendance</Typography>
      {visible.map((a) => (
        <Typography variant="body2" key={keyOf(a)}>
          {nameOf(a.tutorId)}: {display(a.completedClasses)} completed /{" "}
          {a.completedClasses == null
            ? "Not available"
            : Math.max(0, a.allocatedClasses - a.completedClasses)}{" "}
          remaining
        </Typography>
      ))}
      <Records
        headings={["Date", "Tutor", "Subject", "Time", "Duration", "Status"]}
        rows={attendance
          .filter(matches)
          .map((r) => [
            r.startTime ? new Date(r.startTime).toLocaleDateString("en-IN") : "Not available",
            tutorName(r),
            nameOf(r.subjectId),
            r.startTime ? new Date(r.startTime).toLocaleTimeString("en-IN") : "Not available",
            r.duration == null ? "Not available" : `${r.duration} min`,
            r.status || r.type?.join(", ") || "Not available",
          ])}
      />
      <Button disabled sx={{ my: 1 }}>
        Mark attendance — coming soon
      </Button>
      <Typography variant="h6" sx={{ mt: 2 }}>
        Student Fees
      </Typography>
      <Typography variant="body2">
        Package: {money(data.studentBilling?.totalAmount)} · Received:{" "}
        {money(data.studentBilling?.paidAmount)} · Pending:{" "}
        {money(data.studentBilling?.pendingAmount)}
      </Typography>
      <Typography variant="h6" sx={{ mt: 3 }}>
        Tutor Payments
      </Typography>
      <Records
        headings={["Tutor", "Estimated cost", "Earned / payable", "Paid", "Pending"]}
        rows={visible.map((a) => [
          nameOf(a.tutorId),
          money(estimatedCost(a)),
          money(a.earnedAmount),
          money(a.paidAmount),
          money(a.pendingAmount),
        ])}
      />
      <Typography variant="subtitle1" sx={{ mt: 2 }}>
        Payment history
      </Typography>
      <Records
        headings={["Date", "Tutor", "Amount", "Status", "Reference"]}
        rows={history
          .filter(matches)
          .map((r) => [
            r.paymentDate ? new Date(r.paymentDate).toLocaleDateString("en-IN") : "Not available",
            tutorName(r),
            money(r.amount),
            r.status || "Not available",
            r.reference || "Not available",
          ])}
      />
      <Button disabled>Record payment — coming soon</Button>
    </Box>
  );
}
